/**
 * Custom hook: all the side-effects (timers, auto-refresh, session check, backup reminder).
 * Keeps MainApp slim by moving imperative useEffect blocks here.
 */

import { useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import { todayISO } from '../utils/helpers';
import { isSessionValid } from '../utils/session';

export const useAppEffects = ({
  user,
  location,
  isManager,
  activeTab,
  tipDate,
  setTipDate,
  setWaiters,
  setEntries,
  setClosedDays,
  setUsers,
  setBillardEntries,
  setClothingItems,
  setJobPositions,
  setClothingTypes,
  setCurrentDateTime,
  setShowBackupReminder,
  showDeletedWaiters,
  showDeletedEntries,
  showDeletedBillard,
  showDeletedClothing,
  fetchBackupData,
  fetchAuditLog,
  fetchGlassTypes,
  onLogout,
}) => {
  // ---- Fetch data (enhanced version with soft delete support) ----
  const fetchData = useCallback(async () => {
    try {
      const [waitersRes, entriesRes, closedRes] = await Promise.all([
        axios.get(`${API}/waiters?location=${location}&include_deleted=${showDeletedWaiters}`),
        axios.get(`${API}/entries?location=${location}&include_deleted=${showDeletedEntries}`),
        axios.get(`${API}/closed-days?location=${location}`),
      ]);
      setWaiters(waitersRes.data);
      setEntries(entriesRes.data);
      setClosedDays(closedRes.data);

      if (isManager) {
        const usersRes = await axios.get(`${API}/users?location=${location}`);
        setUsers(usersRes.data);
      }

      if (location === 'nowysacz') {
        const billardRes = await axios.get(`${API}/billard?location=${location}&include_deleted=${showDeletedBillard}`);
        setBillardEntries(billardRes.data);
      }

      const clothingRes = await axios.get(`${API}/clothing?location=${location}&include_deleted=${showDeletedClothing}`);
      setClothingItems(clothingRes.data);

      const [positionsRes, typesRes] = await Promise.all([
        axios.get(`${API}/job-positions?location=${location}`),
        axios.get(`${API}/clothing-types?location=${location}`),
      ]);
      setJobPositions(positionsRes.data);
      setClothingTypes(typesRes.data);

      if (isManager) {
        fetchBackupData();
        fetchAuditLog();
      }
    } catch (e) {
      console.error('❌ Error fetching data:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isManager, location,
    showDeletedWaiters, showDeletedEntries, showDeletedBillard, showDeletedClothing,
  ]);

  // Initial fetch + refetch on dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh waiters on employees/manager tab
  useEffect(() => {
    if (activeTab === 'waiters' || activeTab === 'manager') {
      const interval = setInterval(() => { fetchData(); }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchData]);

  // Backup reminder - currently disabled
  useEffect(() => {
    setShowBackupReminder(false);
  }, [setShowBackupReminder]);

  // Tick clock every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, [setCurrentDateTime]);

  // Auto-update date when day changes
  useEffect(() => {
    const interval = setInterval(() => {
      const currentDate = todayISO();
      if (tipDate !== currentDate && activeTab === 'add') {
        setTipDate(currentDate);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [tipDate, activeTab, setTipDate]);

  // Session validity check
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isSessionValid()) onLogout();
    }, 10000);
    return () => clearInterval(interval);
  }, [onLogout]);

  // Fetch glass types on mount / location change
  useEffect(() => {
    fetchGlassTypes();
  }, [fetchGlassTypes]);

  return { fetchData };
};
